<template>
  <div ref="tabs" class="tabs">
    <a v-if="isScroll" class="tab icon" :class="{ off: scrollIndex === 0 }" @click="prev">&#x3c;</a>
    <a
      v-for="(itinerary, i) in itineraries"
      v-show="i >= scrollIndexMin && i <= scrollIndexMax"
      :key="i"
      class="tab"
      :class="{
        booked: itinerary.isBooked && !itinerary.isCanceled,
        canceled: itinerary.isCanceled,
        on: itinerary.index === itineraryIndex,
      }"
      @click="show(itinerary.index)"
    >
      {{ itinerary.index }}
    </a>
    <a v-if="isScroll" class="tab icon right" :class="{ off: scrollIndex === scrollLength - 1 }" @click="next">&#x3e;</a>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";

const props = defineProps({
  itineraries: Array,
  itineraryIndex: Number,
  width: Number,
});

const emit = defineEmits(["update:itineraryIndex"]);

const tabs = ref(null);
const ro = ref(null);
const scrollIndex = ref(null);
const scrollIndexMin = ref(null);
const scrollIndexMax = ref(null);
const scrollVisible = ref(5);

const isScroll = computed(() => {
  return scrollLength.value > scrollVisible.value;
});

const scrollLength = computed(() => {
  return props.itineraries ? props.itineraries.length : 0;
});

const id = index => {
  for (let i = 0; i < scrollLength.value; i++) {
    if (props.itineraries[i].index === index) {
      return i;
    }
  }
};

const init = () => {
  scrollIndexMin.value = null;
  scrollIndexMax.value = null;
  scrollIndex.value = id(props.itineraryIndex);

  if (scrollIndexMin.value === null || scrollIndexMax.value === null || scrollIndex.value < scrollIndexMin.value || scrollIndex.value > scrollIndexMax.value) {
    scrollIndex.value = id(props.itineraryIndex);
    scrollIndexMin.value = scrollIndex.value < scrollVisible.value ? 0 : scrollIndex.value - scrollVisible.value + 1;
    scrollIndexMax.value = scrollIndex.value < scrollVisible.value ? scrollVisible.value - 1 : scrollIndex.value;
  }
};

const next = () => {
  if (scrollIndex.value < scrollLength.value - 1) {
    scrollIndex.value++;
    emit("update:itineraryIndex", parseInt(props.itineraries[scrollIndex.value].index));

    if (scrollIndex.value > scrollIndexMax.value) {
      scrollIndexMin.value++;
      scrollIndexMax.value++;
    }
  }
};

const prev = () => {
  if (scrollIndex.value > 0) {
    scrollIndex.value--;
    emit("update:itineraryIndex", parseInt(props.itineraries[scrollIndex.value].index));

    if (scrollIndex.value <= scrollIndexMax.value - scrollVisible.value) {
      scrollIndexMin.value--;
      scrollIndexMax.value--;
    }
  }
};

const resize = () => {
  if (tabs.value) {
    const w = tabs.value.offsetWidth;
    const newScrollVisible = Math.floor(w / 53) - 2;

    // Only call init if scrollVisible has changed
    if (newScrollVisible !== scrollVisible.value) {
      scrollVisible.value = newScrollVisible;

      // Use nextTick to ensure DOM updates are complete before triggering init
      nextTick(() => {
        init();
      });
    }
  }
};

const show = itineraryIndex => {
  if (props.itineraryIndex === itineraryIndex) return;
  emit("update:itineraryIndex", itineraryIndex);
};

onMounted(() => {
  ro.value = new ResizeObserver(() => {
    // Throttle resize events using requestAnimationFrame
    requestAnimationFrame(resize);
  });
  ro.value.observe(tabs.value);
  init();
});

onBeforeUnmount(() => {
  if (ro.value) {
    ro.value.unobserve(tabs.value);
  }
});

watch(() => props.itineraryIndex, init);
</script>
